// Global Theme Configuration
// Modern Light Theme - Fixed Light UI

export const colors = {
  // Primary brand color - Blue
  primary: '#2563eb', // Blue 600
  primaryHover: '#1d4ed8', // Blue 700
  primaryLight: '#dbeafe', // Blue 100
  primaryDark: '#1e40af', // Blue 800

  // Background colors - Light theme
  bgPrimary: '#ffffff',
  bgSecondary: '#f9fafb', // Gray 50
  bgTertiary: '#f3f4f6', // Gray 100
  bgHover: '#f3f4f6',
  bgDark: '#f3f4f6', // Light gray background for page containers

  // Glass effect backgrounds
  glassLight: 'rgba(255, 255, 255, 0.8)',

  // Sidebar colors
  sidebarBg: '#1f2937', // Gray 800
  sidebarHover: '#374151', // Gray 700
  sidebarActive: '#2563eb',

  // Text colors
  textPrimary: '#111827', // Gray 900
  textSecondary: '#6b7280', // Gray 500
  textTertiary: '#9ca3af', // Gray 400
  textMuted: '#d1d5db', // Gray 300

  // Sidebar text
  textLight: '#ffffff',
  textLightSecondary: '#d1d5db',

  // Border colors
  border: '#e5e7eb', // Gray 200
  borderLight: '#f3f4f6', // Gray 100
  borderDark: '#d1d5db', // Gray 300

  // Status colors
  success: '#10b981', // Green 500
  successLight: '#d1fae5', // Green 100
  warning: '#f59e0b', // Amber 500
  warningLight: '#fef3c7', // Amber 100
  error: '#ef4444', // Red 500
  errorLight: '#fee2e2', // Red 100
  info: '#3b82f6', // Blue 500
  infoLight: '#dbeafe', // Blue 100

  // Accent colors
  accent: '#8b5cf6', // Violet 500
  accentLight: '#ede9fe', // Violet 100
};

export const effects = {
  // Light theme shadows - subtle and modern
  shadowSm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  shadowMd: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  shadowLg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  shadowXl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  shadow2xl: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',

  // Focus rings
  focusRing: '0 0 0 3px rgba(37, 99, 235, 0.1)',
  focusRingError: '0 0 0 3px rgba(239, 68, 68, 0.1)',
  focusRingSuccess: '0 0 0 3px rgba(16, 185, 129, 0.1)',

  // Hover elevations
  hoverElevation: '0 8px 16px -4px rgba(0, 0, 0, 0.12)',

  // Card styles for light theme
  card: {
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '12px',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
  },

  cardHover: {
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    transform: 'translateY(-2px)',
  },
};

export const spacing = {
  xs: '4px',
  sm: '8px',
  md: '16px',
  lg: '24px',
  xl: '32px',
  xxl: '48px',
  xxxl: '64px',
};

export const borderRadius = {
  sm: '8px',
  md: '12px',
  lg: '16px',
  xl: '20px',
  card: '12px', // For cards and tiles
  pill: '999px', // Only for true pill buttons
};

export const typography = {
  fontFamily: {
    sans: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    serif: 'Georgia, "Times New Roman", serif',
    mono: '"SF Mono", Monaco, "Cascadia Code", "Roboto Mono", Consolas, monospace',
  },

  fontSize: {
    xs: '11px',
    sm: '13px',
    base: '14px',
    md: '15px',
    lg: '16px',
    xl: '18px',
    '2xl': '22px',
    '3xl': '28px',
    '4xl': '36px',
  },

  fontWeight: {
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    black: 900,
  },
};

export const transitions = {
  fast: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  normal: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  slow: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  bounce: 'all 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
};

// Component-specific styles
export const components = {
  // Primary button
  button: {
    borderRadius: borderRadius.md,
    padding: '10px 20px',
    background: colors.primary,
    border: 'none',
    color: '#ffffff',
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    cursor: 'pointer',
    transition: transitions.normal,
    boxShadow: effects.shadowSm,
  },

  buttonHover: {
    background: colors.primaryHover,
    boxShadow: effects.shadowMd,
    transform: 'translateY(-1px)',
  },

  // Secondary button
  buttonSecondary: {
    borderRadius: borderRadius.md,
    padding: '10px 20px',
    background: colors.bgPrimary,
    border: `1px solid ${colors.border}`,
    color: colors.textPrimary,
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    cursor: 'pointer',
    transition: transitions.normal,
  },

  buttonSecondaryHover: {
    background: colors.bgSecondary,
    borderColor: colors.borderDark,
  },

  // Card
  card: {
    background: colors.bgPrimary,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.card,
    padding: spacing.xl,
    boxShadow: effects.shadowSm,
    transition: transitions.normal,
  },

  cardHover: {
    boxShadow: effects.shadowMd,
    transform: 'translateY(-2px)',
  },

  // Input field
  input: {
    background: colors.bgPrimary,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    padding: '10px 14px',
    color: colors.textPrimary,
    fontSize: typography.fontSize.base,
    transition: transitions.normal,
  },

  inputFocus: {
    borderColor: colors.primary,
    boxShadow: effects.focusRing,
    outline: 'none',
  },
};

export default {
  colors,
  effects,
  spacing,
  borderRadius,
  typography,
  transitions,
  components,
};
